import type { PartialRoleResponse } from '@kfolks/types'
import { useFilteredQuery } from '@/shared/hooks/useFilteredQuery'
import { FilterBar } from '@/shared/components/molecules/FilterBar'

const PERMISSION_FILTERS = [
	{ key: 'name', label: 'Role Name', type: 'text' as const },
	{ key: 'providerId', label: 'Provider ID', type: 'text' as const },
	{
		key: 'isActive',
		label: 'Status',
		type: 'select' as const,
		options: [
			{ label: 'Active', value: 'true' },
			{ label: 'Inactive', value: 'false' },
		],
	},
]

export function PermissionsPage() {
	const {
		data: roles,
		isLoading,
		tempFilters,
		setTempFilters,
		applyFilters,
		clearFilters,
	} = useFilteredQuery<PartialRoleResponse>({
		endpoint: '/roles',
		queryKey: 'roles',
	})

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
			</div>
		)
	}

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<h1 className="text-2xl font-bold text-foreground">Permissions & Roles</h1>
				<button
					type="button"
					className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
				>
					Create Role
				</button>
			</div>

			<FilterBar
				filters={PERMISSION_FILTERS}
				values={tempFilters}
				onChange={setTempFilters}
				onApply={applyFilters}
				onClear={clearFilters}
			/>

			<div className="bg-white shadow rounded-lg overflow-hidden">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-muted">
						<tr>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Role</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Provider</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Permissions</th>
							<th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{roles.map((role) => (
							<tr key={role.roleId}>
								<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{role.name}</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{role.providerId}</td>
								<td className="px-6 py-4 text-sm text-muted-foreground">{role.permissions?.join(', ')}</td>
								<td className="px-6 py-4 whitespace-nowrap text-right text-sm">
									<button type="button" className="text-indigo-600 hover:text-indigo-900 mr-3">
										Edit
									</button>
									<button type="button" className="text-red-600 hover:text-red-900">
										Delete
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
				{roles.length === 0 && (
					<div className="text-center py-12 text-muted-foreground">No roles found. Create one to get started.</div>
				)}
			</div>
		</div>
	)
}
